import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

const STORAGE_KEY = "kontractai_client_scope";
const ALL_SENTINEL = "all" as const;

export type ClientChoice = number | typeof ALL_SENTINEL;

export interface ClientScopeOrg {
  id: string;
  name: string;
  org_type: "firm" | "client" | "internal";
  client_id: number | null;
}

export interface ClientScopeAvailable {
  id: number;
  name: string;
}

export type Edition = "firm" | "in_house";

export interface ClientScopeValue {
  loading: boolean;
  organization: ClientScopeOrg | null;
  availableClients: ClientScopeAvailable[];
  canPickAll: boolean;
  selected: ClientChoice;
  setSelected: (next: ClientChoice) => void;
  refresh: () => Promise<void>;
  edition: Edition;
  // Whether the AI usage and costs screen exists on this deployment. Decided
  // by the server, never inferred from `edition` — see aiSpendAvailability.js.
  aiSpendAvailable: boolean;
  // Modules switched off on this instance by the KAI_DISABLED_MODULES deployment
  // setting ("drafting", "docint"). Server-decided, same as aiSpendAvailable.
  disabledModules: string[];
  tenantPrefix: string;
}

const noopValue: ClientScopeValue = {
  loading: true,
  organization: null,
  availableClients: [],
  canPickAll: false,
  selected: ALL_SENTINEL,
  setSelected: () => {},
  refresh: async () => {},
  edition: "firm",
  aiSpendAvailable: false,
  disabledModules: [],
  tenantPrefix: "",
};

const ClientScopeCtx = createContext<ClientScopeValue>(noopValue);

export function readSelectedFromStorage(): ClientChoice {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw || raw === ALL_SENTINEL) return ALL_SENTINEL;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : ALL_SENTINEL;
  } catch {
    return ALL_SENTINEL;
  }
}

function writeSelectedToStorage(choice: ClientChoice) {
  try {
    if (choice === ALL_SENTINEL) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, String(choice));
  } catch {
    /* sessionStorage unavailable — safe to ignore; header just won't be sent */
  }
}

export function ClientScopeProvider({ token, children }: { token: string | null; children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState<ClientScopeOrg | null>(null);
  const [availableClients, setAvailableClients] = useState<ClientScopeAvailable[]>([]);
  const [canPickAll, setCanPickAll] = useState(false);
  const [selected, setSelectedState] = useState<ClientChoice>(readSelectedFromStorage());
  const [edition, setEdition] = useState<Edition>("firm");
  // Defaults to FALSE. Unknown must read as "not available": the direction
  // that hides a screen is safe, the direction that shows it is not.
  const [aiSpendAvailable, setAiSpendAvailable] = useState(false);
  // Defaults to EMPTY — nothing disabled — which is the OPPOSITE default to
  // aiSpendAvailable above, deliberately. The two are asymmetric because the
  // consequences are:
  //   * aiSpendAvailable unknown  -> hide. Showing a screen that must not exist
  //     on a shared estate would leak another firm's spending.
  //   * disabledModules unknown   -> SHOW. Every instance except one has these
  //     modules on, so defaulting to "something is disabled" would hide working
  //     features on the VPS, on Azure and on every future customer the moment
  //     this fetch hiccuped. The backend route guards are the real control, so
  //     an optimistic UI that offers a disabled module merely gets a 404 — a
  //     visible, contained failure — whereas a pessimistic UI silently removes
  //     two modules from estates that never asked for it.
  const [disabledModules, setDisabledModules] = useState<string[]>([]);
  const [tenantPrefix, setTenantPrefix] = useState<string>("");

  const refresh = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/users/me/client-scope", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data = await res.json();
      setOrganization(data.organization || null);
      setAvailableClients(data.available_clients || []);
      setCanPickAll(!!data.can_pick_all);
      setEdition(data.edition === "in_house" ? "in_house" : "firm");
      setAiSpendAvailable(!!data.ai_spend_available);
      setDisabledModules(Array.isArray(data.disabled_modules) ? data.disabled_modules : []);
      setTenantPrefix((data.tenant_prefix || "").toUpperCase());

      // Reconcile persisted selection against the freshly loaded scope. If the
      // stored client is no longer in the user's scope (revoked, etc.), fall
      // back to "all" rather than sending a header that the backend will 403.
      const stored = readSelectedFromStorage();
      const ids = new Set<number>((data.available_clients || []).map((c: ClientScopeAvailable) => c.id));
      let next: ClientChoice = ALL_SENTINEL;
      if (stored !== ALL_SENTINEL && ids.has(stored)) next = stored;
      else if (!data.can_pick_all && (data.available_clients || []).length === 1) next = data.available_clients[0].id;
      setSelectedState(next);
      writeSelectedToStorage(next);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setSelected = useCallback((next: ClientChoice) => {
    setSelectedState(next);
    writeSelectedToStorage(next);
  }, []);

  const value = useMemo<ClientScopeValue>(
    () => ({ loading, organization, availableClients, canPickAll, selected, setSelected, refresh, edition, tenantPrefix, aiSpendAvailable, disabledModules }),
    [loading, organization, availableClients, canPickAll, selected, setSelected, refresh, edition, tenantPrefix, aiSpendAvailable, disabledModules]
  );

  return <ClientScopeCtx.Provider value={value}>{children}</ClientScopeCtx.Provider>;
}

export function useClientScope(): ClientScopeValue {
  return useContext(ClientScopeCtx);
}

/**
 * Is this module switched off on this instance?
 *
 * One helper rather than `disabledModules.includes("drafting")` repeated per
 * screen: the inventory has several entry points per module, and a literal
 * repeated at each of them is the two-copies-of-one-string failure this
 * codebase has been bitten by before. Case-insensitive so a deployment
 * variable written `Drafting` behaves the same as `drafting`.
 */
export function useModuleDisabled(moduleKey: string): boolean {
  const { disabledModules } = useClientScope();
  const needle = moduleKey.toLowerCase();
  return (disabledModules || []).some((m) => String(m).toLowerCase() === needle);
}

export const CLIENT_SCOPE_STORAGE_KEY = STORAGE_KEY;
export const CLIENT_SCOPE_ALL = ALL_SENTINEL;
